import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Mail, Send, Clock, CheckCircle, XCircle, RefreshCw, Calendar, User, Building2, ChevronDown, ChevronUp } from 'lucide-react';

const EmailActivityMonitor = ({ isOpen, onClose, adminToken }) => {
  const [loading, setLoading] = useState(false);
  const [emailData, setEmailData] = useState(null);
  const [followupEmails, setFollowupEmails] = useState([]);
  const [practiceEmails, setPracticeEmails] = useState([]);
  const [activeTab, setActiveTab] = useState('practices');
  const [expandedPractices, setExpandedPractices] = useState({});

  const fetchEmailActivity = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-activity`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmailData(data);
      }
    } catch (error) {
      console.error('Error fetching email activity:', error);
    }
    setLoading(false);
  };

  const fetchFollowupEmails = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/followup-emails`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFollowupEmails(data.followup_emails || []);
      }
    } catch (error) {
      console.error('Error fetching followup emails:', error);
    }
  };

  const fetchPracticeEmails = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-activity/by-practice`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPracticeEmails(data.practices || []);
      }
    } catch (error) {
      console.error('Error fetching practice emails:', error);
    }
  };

  useEffect(() => {
    if (isOpen && adminToken) {
      fetchEmailActivity();
      fetchFollowupEmails();
      fetchPracticeEmails();
    }
  }, [isOpen, adminToken]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return <Badge className="bg-green-100 text-green-800 border-green-300"><CheckCircle className="w-3 h-3 mr-1" />Sent</Badge>;
      case 'scheduled':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300"><Clock className="w-3 h-3 mr-1" />Scheduled</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800 border-red-300"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      case 'pending':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-300"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'followup':
        return <Badge className="bg-purple-100 text-purple-800">Follow-up</Badge>;
      case 'trial_reminder':
        return <Badge className="bg-blue-100 text-blue-800">Trial Reminder</Badge>;
      case 'weekly_summary':
        return <Badge className="bg-indigo-100 text-indigo-800">Weekly Summary</Badge>;
      case 'welcome':
        return <Badge className="bg-green-100 text-green-800">Welcome</Badge>;
      case 'exit_survey':
        return <Badge className="bg-orange-100 text-orange-800">Thank You</Badge>;
      case 'nudge':
        return <Badge className="bg-teal-100 text-teal-800">Nudge/Resend</Badge>;
      default:
        return <Badge>{type}</Badge>;
    }
  };

  const togglePractice = (practiceId) => {
    setExpandedPractices(prev => ({
      ...prev,
      [practiceId]: !prev[practiceId]
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Mail className="w-6 h-6 text-blue-600" />
            Email Activity Monitor
          </DialogTitle>
        </DialogHeader>

        <div className="flex justify-end mb-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => { fetchEmailActivity(); fetchFollowupEmails(); fetchPracticeEmails(); }}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="practices">By Practice</TabsTrigger>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="followups">Follow-ups</TabsTrigger>
            <TabsTrigger value="activity">Recent Activity</TabsTrigger>
          </TabsList>

          {/* By Practice Tab */}
          <TabsContent value="practices" className="space-y-4">
            <p className="text-sm text-gray-500 mb-4">
              View all emails sent to each practice. Click on a practice to see details.
            </p>
            <div className="space-y-2">
              {practiceEmails.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No practices found.</p>
                </div>
              ) : (
                practiceEmails.map((practice, index) => (
                  <div key={index} className="border rounded-lg overflow-hidden">
                    <div 
                      className="p-4 cursor-pointer hover:bg-gray-50 flex justify-between items-center"
                      onClick={() => togglePractice(practice.practiceId)}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{practice.practiceName}</span>
                          <Badge variant="outline" className="ml-2">
                            {practice.totalEmailsSent} emails
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500">{practice.practiceEmail}</p>
                        <p className="text-xs text-gray-400">Registered: {formatDate(practice.createdAt)}</p>
                      </div>
                      <div>
                        {expandedPractices[practice.practiceId] ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                    
                    {expandedPractices[practice.practiceId] && (
                      <div className="border-t bg-gray-50 p-4">
                        {practice.emails.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-2">No emails sent yet</p>
                        ) : (
                          <div className="space-y-2">
                            {practice.emails.map((email, emailIndex) => (
                              <div key={emailIndex} className="bg-white border rounded p-3 flex justify-between items-center">
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    {getTypeBadge(email.type)}
                                    {getStatusBadge(email.status)}
                                  </div>
                                  <p className="text-sm font-medium">{email.subject}</p>
                                </div>
                                <div className="text-right text-sm text-gray-500">
                                  {formatDate(email.sentAt)}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            {emailData?.summary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Follow-up Emails Card */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Follow-up Emails</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-purple-600">
                      {emailData.summary.followup_emails?.sent || 0}
                    </div>
                    <p className="text-xs text-gray-500">
                      Sent • {emailData.summary.followup_emails?.pending || 0} pending
                    </p>
                    {emailData.summary.followup_emails?.failed > 0 && (
                      <p className="text-xs text-red-500">
                        {emailData.summary.followup_emails?.failed} failed
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Trial Reminders Card */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Trial Reminders</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">
                      {(emailData.summary.trial_reminders?.day3_sent || 0) + (emailData.summary.trial_reminders?.day12_sent || 0)}
                    </div>
                    <p className="text-xs text-gray-500">
                      Day 3: {emailData.summary.trial_reminders?.day3_sent || 0} • Day 12: {emailData.summary.trial_reminders?.day12_sent || 0}
                    </p>
                  </CardContent>
                </Card>

                {/* Welcome Emails Card */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Welcome Emails</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {emailData.summary.welcome_emails?.sent || 0}
                    </div>
                    <p className="text-xs text-gray-500">Total sent</p>
                  </CardContent>
                </Card>

                {/* Weekly Summaries Card */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Weekly Summaries</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-indigo-600">
                      {emailData.summary.weekly_summaries?.sent || 0}
                    </div>
                    <p className="text-xs text-gray-500">Total sent</p>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Follow-up Emails Tab */}
          <TabsContent value="followups" className="space-y-4">
            <div className="space-y-2">
              {followupEmails.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Mail className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No follow-up emails scheduled yet.</p>
                  <p className="text-sm">Follow-ups are created when procedures are marked as delivered.</p>
                </div>
              ) : (
                followupEmails.map((email, index) => (
                  <div key={index} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{email.patientName || 'Unknown'}</span>
                          {getStatusBadge(email.status)}
                        </div>
                        <p className="text-sm text-gray-600">{email.procedureName}</p>
                        <p className="text-xs text-gray-400">{email.patientEmail}</p>
                      </div>
                      <div className="text-right text-sm">
                        <p className="text-gray-500">
                          <Calendar className="w-3 h-3 inline mr-1" />
                          {email.status === 'sent' ? 'Sent:' : 'Scheduled:'} {formatDate(email.sentAt || email.scheduledFor)}
                        </p>
                        <p className="text-xs text-gray-400">{email.practiceName}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Recent Activity Tab */}
          <TabsContent value="activity" className="space-y-4">
            <div className="space-y-2">
              {!emailData?.email_activity || emailData.email_activity.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Send className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No recent email activity.</p>
                </div>
              ) : (
                emailData.email_activity.map((activity, index) => (
                  <div key={index} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {getTypeBadge(activity.type)}
                          {getStatusBadge(activity.status)}
                        </div>
                        <p className="text-sm font-medium">{activity.subject}</p>
                        <p className="text-xs text-gray-500">
                          To: {activity.recipient_name || activity.recipient}
                        </p>
                      </div>
                      <div className="text-right text-sm text-gray-500">
                        {formatDate(activity.sent_at || activity.scheduled_for)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Email open tracking is enabled via SendGrid. Open rates will be tracked automatically when recipients view emails.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailActivityMonitor;
